import clsx from 'clsx';
import { ArrowRight } from 'lucide-react';
import { TalkdownStep } from './sop-card-utils';

interface TalkdownStepsProps {
  steps: TalkdownStep[];
  activeIndex: number;
}

const STEP_BADGE =
  'inline-flex w-12 items-center justify-center rounded-full py-0.5 text-xs font-medium';

const STEP_CIRCLE =
  'mr-2 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold';

const NOW_COLOR = 'bg-primary-600 text-white';
const SENT_COLOR = 'bg-status-emerald text-white';
const NEXT_COLOR =
  'bg-onlight-bg-2 text-onlight-text-3 dark:bg-ondark-bg-3 dark:text-ondark-text-2';

function stepBadge(isNow: boolean, isDone: boolean) {
  if (isNow) {
    return { label: 'Now', className: NOW_COLOR };
  }
  if (isDone) {
    return {
      label: 'Sent',
      className: 'bg-status-emerald/15 text-status-emerald',
    };
  }
  return { label: 'Next', className: NEXT_COLOR };
}

function circleColor(isNow: boolean, isDone: boolean) {
  if (isNow) {
    return NOW_COLOR;
  }
  if (isDone) {
    return SENT_COLOR;
  }
  return NEXT_COLOR;
}

export function TalkdownSteps({ steps, activeIndex }: TalkdownStepsProps) {
  if (steps.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-y-4">
      <h5 className="text-onlight-text-1 dark:text-ondark-text-1 font-semibold">
        Talkdown Steps
      </h5>
      <ol className="flex flex-wrap items-center gap-x-3 gap-y-3">
        {steps.map((step, i) => {
          const isDone = step.index < activeIndex;
          const isNow = step.index === activeIndex;
          const badge = stepBadge(isNow, isDone);

          return (
            <li key={step.index} className="flex items-center gap-2">
              {i > 0 && (
                <ArrowRight className="text-onlight-text-3 dark:text-ondark-text-3 h-4 w-4 shrink-0" />
              )}
              <span className={clsx(STEP_CIRCLE, circleColor(isNow, isDone))}>
                {step.index}
              </span>
              <div className="flex flex-col items-start gap-1">
                <span className="text-onlight-text-1 dark:text-ondark-text-1 text-sm font-medium">
                  {step.label}
                </span>
                <span className={clsx(STEP_BADGE, badge.className)}>
                  {badge.label}
                </span>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export default TalkdownSteps;
